import { Alert, DatePicker, Form, Input, Modal, Space, Tag, notification } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo, useState } from 'react';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { useCurrentRoles } from 'hooks/use-current-roles';
import { CREATOR_BRANCH_LABEL, labelOf } from 'pages/star-shared/labels';
import { mapStarError } from 'pages/star-shared/star-error';
import { RecruitCandidate, RecruitView } from 'pages/star-shared/types';
import { extractArray } from 'pages/star-shared/utils';
import RemoteSearchSelect, {
  PickedValue,
  StarAccountSelect,
} from 'pages/star-shared/components/remote-search-select';
import { colors } from 'theme/tokens';

interface Props {
  open: boolean;
  /** Số ngày được lùi ngày bắt đầu (cấu hình recruit.registerWindowDays). */
  registerWindowDays: number;
  onClose: () => void;
  onCreated: (recruit: RecruitView | null) => void;
}

interface FormValue {
  creator?: PickedValue;
  recruiter?: PickedValue;
  startDate?: Dayjs;
  note?: string;
}

const fetchCandidates = async (query: string) => {
  const res: any = await AxiosUtils.get(API.starsRecruitCandidates, query ? { q: query } : {});
  return extractArray<RecruitCandidate>(res);
};

const candidateName = (c: RecruitCandidate) => c.nickName || c.username || `#${c.id}`;

// "Thêm phiếu" — POST /stars/admin/recruits (SPEC-NHIEM-VU-MO-RONG.md §3.2).
const RecruitCreateModal = ({ open, registerWindowDays, onClose, onCreated }: Props) => {
  const [form] = Form.useForm<FormValue>();
  const [loading, setLoading] = useState(false);
  const { userId } = useCurrentRoles();
  const creator = Form.useWatch('creator', form);

  const today = dayjs().startOf('day');
  const earliest = today.subtract(registerWindowDays, 'day');

  // Người tạo phiếu không được là người tuyển; người tuyển không trùng Creator.
  const blocked = useMemo(() => {
    const map: Record<number, string> = {};
    if (userId) map[userId] = 'Bạn đang tạo phiếu';
    if (creator?.value) map[creator.value] = 'Là Creator của phiếu';
    return map;
  }, [userId, creator?.value]);

  const handleClose = () => {
    if (loading) return;
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: FormValue) => {
    if (!values.creator || !values.recruiter || !values.startDate) return;
    setLoading(true);
    try {
      const res: any = await AxiosUtils.post(API.starsRecruits, {
        creatorUserId: values.creator.value,
        recruiterUserId: values.recruiter.value,
        startDate: dayjs(values.startDate).format('YYYY-MM-DD'),
        note: values.note?.trim() || undefined,
      });
      notification.success({
        message: `Đã tạo phiếu: ${values.creator.label} — người tuyển ${values.recruiter.label}.`,
        description: 'Máy tự chấm các mốc mỗi sáng; cần thì bấm "Xác nhận tay".',
      });
      form.resetFields();
      onCreated(res?.data && typeof res.data === 'object' ? (res.data as RecruitView) : null);
      onClose();
    } catch (err) {
      notification.error({ message: mapStarError(err) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Thêm phiếu tuyển dụng"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnHidden
      maskClosable={false}
      width={600}
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            <li>
              Ngày bắt đầu không ở tương lai và không quá {registerWindowDays} ngày trước hôm nay.
            </li>
            <li>
              Người tạo phiếu không được là người tuyển. Người tuyển là SMOD/Quản lý thì chỉ Admin tạo.
            </li>
            <li>Mỗi Creator chỉ có một phiếu đang hiệu lực. Creator Online không tham gia.</li>
          </ul>
        }
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        className="custom-form"
        initialValues={{ startDate: today }}
      >
        <Form.Item
          label="Creator Studio"
          name="creator"
          rules={[{ required: true, message: 'Chọn Creator.' }]}
          extra="Chỉ Creator Dance và Creator NPC."
        >
          <RemoteSearchSelect<RecruitCandidate>
            placeholder="Gõ tên hoặc username"
            fetchRows={fetchCandidates}
            toOption={(c) => ({
              value: c.id,
              name: candidateName(c),
              disabled: !!c.hasActiveRecruit,
              label: (
                <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
                  <Space size={6} wrap>
                    <span style={{ fontWeight: 600 }}>{candidateName(c)}</span>
                    {c.branch && (
                      <Tag color={c.branch === 'DANCE' ? 'purple' : 'cyan'}>
                        {labelOf(CREATOR_BRANCH_LABEL, c.branch)}
                      </Tag>
                    )}
                    {c.hasActiveRecruit && <Tag>Đã có phiếu</Tag>}
                  </Space>
                  <span style={{ color: colors.textSecondary, fontSize: 12 }}>
                    {[
                      c.username && `@${c.username}`,
                      c.tiktokUid ? `TikTok ${c.tiktokUid}` : 'Chưa có UID TikTok',
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
              ),
            })}
          />
        </Form.Item>
        <Form.Item
          label="Người tuyển (Nhân viên)"
          name="recruiter"
          dependencies={['creator']}
          rules={[
            { required: true, message: 'Chọn người tuyển.' },
            ({ getFieldValue }) => ({
              validator: (_, value?: PickedValue) => {
                if (value?.value && userId && value.value === userId) {
                  return Promise.reject(new Error('Người tạo phiếu không được là người tuyển.'));
                }
                if (value?.value && value.value === getFieldValue('creator')?.value) {
                  return Promise.reject(new Error('Người tuyển không được trùng Creator.'));
                }
                return Promise.resolve();
              },
            }),
          ]}
          extra="HR hoặc Nhân viên được chỉ định; ví Sao nhóm Nhân viên, đang hoạt động."
        >
          <StarAccountSelect group="STAFF" blocked={blocked} placeholder="Gõ tên để tìm" />
        </Form.Item>
        <Form.Item
          label="Ngày bắt đầu"
          name="startDate"
          rules={[{ required: true, message: 'Chọn ngày bắt đầu.' }]}
          extra={`Mốc tính từ ngày này. Chọn được từ ${earliest.format('DD/MM/YYYY')} tới hôm nay.`}
        >
          <DatePicker
            format="DD/MM/YYYY"
            style={{ width: '100%' }}
            disabledDate={(d) => d.isAfter(today, 'day') || d.isBefore(earliest, 'day')}
          />
        </Form.Item>
        <Form.Item label="Ghi chú (không bắt buộc)" name="note" rules={[{ max: 255 }]}>
          <Input.TextArea
            rows={2}
            maxLength={255}
            showCount
            placeholder="VD: tuyển qua hội chợ việc làm"
          />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text="Tạo phiếu"
            bgColor="secondary-green"
            htmlType="submit"
            loading={loading}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default RecruitCreateModal;
